import React from 'react';
import { LinkButton } from "@/components/LinkButton";
import { MessageSquare, Award, RefreshCw, Star, ThumbsUp, Zap, ArrowRight, Lightbulb, Target, CheckCircle, BarChart } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section - Style moderne avec gradients */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 py-24 lg:py-32">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full text-blue-100 mb-6">
              <span className="font-medium">FeedMyBack • La plateforme de feedback d'excellence</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Transformez vos <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-yellow-100">interactions</span> en opportunités de <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-yellow-100">croissance</span>
            </h1>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Une plateforme complète pour créer, gérer et exploiter des feedbacks de qualité dans votre parcours professionnel
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-5">
              <LinkButton to="/auth?tab=register" className="bg-white text-blue-700 hover:bg-blue-50 text-lg py-5 px-10 rounded-xl shadow-lg transition-all hover:shadow-xl">
                Créer un compte gratuitement
              </LinkButton>
              <LinkButton 
                to="/auth" 
                variant="outline" 
                className="border-2 border-white bg-blue-600/20 text-white hover:bg-white/20 text-lg py-5 px-10 rounded-xl shadow-lg transition-all font-medium"
              >
                Se connecter <ArrowRight className="ml-2 h-5 w-5" />
              </LinkButton>
            </div>
          </div>
        </div>
        
        {/* Decoration abstrait en bas du hero */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-transparent to-white/5"></div>
      </section>

      {/* Section Bénéfices avec design moderne */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Une solution complète de gestion de feedback</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Pour les recruteurs, managers, équipes RH et candidats qui cherchent à optimiser leurs interactions
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 transition-all hover:shadow-lg">
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 w-14 h-14 rounded-xl flex items-center justify-center mb-5">
                <MessageSquare className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Feedback Structuré</h3>
              <p className="text-gray-600">Créez des retours détaillés avec notre assistant guidé pour des commentaires pr��cis et actionnables.</p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 transition-all hover:shadow-lg">
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 w-14 h-14 rounded-xl flex items-center justify-center mb-5">
                <Target className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Demandes Ciblées</h3>
              <p className="text-gray-600">Sollicitez des feedbacks spécifiques auprès de recruteurs ou candidats pour des réponses plus pertinentes.</p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 transition-all hover:shadow-lg">
              <div className="bg-gradient-to-r from-violet-600 to-violet-500 w-14 h-14 rounded-xl flex items-center justify-center mb-5">
                <BarChart className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Analyses et Insights</h3>
              <p className="text-gray-600">Visualisez vos progrès et tendances grâce à des tableaux de bord analytiques détaillés.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Comment ça marche - Redesign moderne */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Une expérience de feedback complète</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Des outils adaptés à chaque besoin dans le processus de feedback
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16">
            <div className="relative">
              <div className="absolute top-0 bottom-0 left-8 md:left-12 w-px bg-gradient-to-b from-blue-400 to-blue-600"></div>
              
              <div className="space-y-12">
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md border-4 border-blue-100">
                    <Lightbulb className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Création de Feedback</h3>
                  <p className="text-gray-600">Un assistant intelligent pour vous guider dans la rédaction de feedbacks structurés, positifs et constructifs.</p>
                </div>
                
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md border-4 border-blue-100">
                    <Star className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Contrôle Qualité</h3>
                  <p className="text-gray-600">Des mécanismes de vérification pour assurer des retours pertinents, bienveillants et professionnels.</p>
                </div>
                
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md border-4 border-blue-100">
                    <CheckCircle className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Partage Sécurisé</h3>
                  <p className="text-gray-600">Distribution des feedbacks via email, lien privé ou directement sur la plateforme avec contrôle d'accès.</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute top-0 bottom-0 left-8 md:left-12 w-px bg-gradient-to-b from-orange-400 to-orange-600"></div>
              
              <div className="space-y-12">
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md border-4 border-orange-100">
                    <MessageSquare className="h-7 w-7 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Demande de Feedback</h3>
                  <p className="text-gray-600">Sollicitez facilement des retours spécifiques auprès de recruteurs ou candidats avec des formulaires personnalisables.</p>
                </div>
                
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md border-4 border-orange-100">
                    <ThumbsUp className="h-7 w-7 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Gestion Centralisée</h3>
                  <p className="text-gray-600">Un tableau de bord complet pour suivre toutes vos demandes, réponses et interactions de feedback.</p>
                </div>
                
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md border-4 border-orange-100">
                    <Zap className="h-7 w-7 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Plans d'Action</h3>
                  <p className="text-gray-600">Transformez les feedbacks reçus en stratégies concrètes d'amélioration et objectifs de développement.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages - Section moderne */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Témoignages</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Découvrez comment FeedMyBack transforme l'expérience de feedback professionnel
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl relative">
              <div className="absolute -top-4 -left-4 text-5xl text-blue-400">"</div>
              <p className="text-gray-700 mb-6 pt-4">
                FeedMyBack nous permet enfin de fournir des retours de qualité à tous les candidats. L'outil est intuitif et nous fait gagner un temps précieux tout en améliorant notre marque employeur.
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center mr-4">
                  <span className="text-blue-600 font-bold">SE</span>
                </div>
                <div>
                  <h4 className="font-semibold">Sarah E.</h4>
                  <p className="text-sm text-gray-500">Responsable RH, Tech Innovation</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl relative">
              <div className="absolute -top-4 -left-4 text-5xl text-orange-400">"</div>
              <p className="text-gray-700 mb-6 pt-4">
                Grâce aux feedbacks reçus via la plateforme, j'ai pu comprendre précisément comment améliorer ma présentation et mon parcours candidat. Les retours constructifs ont été déterminants pour mon évolution.
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center mr-4">
                  <span className="text-orange-600 font-bold">TM</span>
                </div>
                <div>
                  <h4 className="font-semibold">Thomas M.</h4>
                  <p className="text-sm text-gray-500">Développeur Frontend</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Style moderne */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Prêt à transformer votre approche du feedback ?</h2>
            <p className="text-xl mb-10 text-blue-100">
              Rejoignez FeedMyBack et contribuez à une expérience professionnelle plus humaine et constructive.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-5">
              <LinkButton to="/auth?tab=register" className="bg-white text-blue-700 hover:bg-blue-50 text-lg py-5 px-10 rounded-xl shadow-lg transition-all hover:shadow-xl">
                Créer un compte gratuitement
              </LinkButton>
              <LinkButton 
                to="/auth" 
                variant="outline" 
                className="border-2 border-white bg-blue-600/20 text-white hover:bg-white/20 text-lg py-5 px-10 rounded-xl shadow-lg transition-all font-medium"
              >
                Se connecter <ArrowRight className="ml-2 h-5 w-5" />
              </LinkButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
